import React from "react";

import Grid from "@material-ui/core/Grid";

import { SubtleBadge } from "components/Badge";
import { FormCollapse } from "components/Form";
import { Row, SpaceBetweenRow } from "components/Layout";

import { FinanceSubModalSection } from "constants/settings";

import { FieldArray, useFormikContext } from "formik";

import { sectionHasErrors } from "lib/form";

import {
  SectionToggleState,
  useBoolean,
  useFieldValue,
  useSectionHelper,
} from "hooks";
import { useHasPaymentPreferenceForBusinessPermission } from "hooks/useHasPermission";
import { Label, withNamespace } from "components/Form/FormikControls";
import { useSelector } from "react-redux";
import { getDefaultBillingSettings } from "selectors";
import { SlimButton } from "components/Button";
import { ButtonIcon } from "components/Button/FontAwesomeButton";
import { faPlus, faTrash } from "@fortawesome/pro-solid-svg-icons";
import { Box, Divider } from "@material-ui/core";
import {
  SaleSubTypeField,
  SaleTypeField,
  SpeciesField,
} from "components/Form/Fields/SaleFilter";
import { v4 as uuidv4 } from "uuid";
import { BankingDetailField } from "components/Form/Fields/BankingDetailField";
import { BusinessField } from "components/Form/Fields/BusinessField";
import { ConfirmDialog } from "components/ConfirmDialog";
import { Tooltip } from "components/Form/FormikControls/Tooltip";

import { Paper } from "components/Paper";

interface SectionProps {
  sectionHelper: SectionToggleState;
  namespace: string;
}

interface PaymentPreferenceRowProps {
  index: number;
  name: string;
  namespace: string;
  removeRow: (index: number) => null;
}

function SaleTypePaymentPreference(props: PaymentPreferenceRowProps) {
  const { index, name, namespace, removeRow } = props;
  const ns = withNamespace(withNamespace(namespace, name), index.toString());

  const [isConfirmDialogOpen, openConfirmDialog, closeConfirmDialog] =
    useBoolean();
  return (
    <>
      <Grid item xs={6} md={3}>
        <SaleTypeField namespace={ns} required />
      </Grid>
      <Grid item xs={6} md={2}>
        <SpeciesField namespace={ns} required />
      </Grid>
      <Grid item xs={6} md={3}>
        <SaleSubTypeField namespace={ns} />
      </Grid>
      <Grid item xs={5} md={3}>
        <BankingDetailField namespace={ns} required />
      </Grid>

      <Grid item xs={1} md={1} container justifyContent="flex-end">
        <Box>
          <SlimButton
            onClick={openConfirmDialog}
            backgroundColor="deleteRed"
            type="button"
          >
            <ButtonIcon icon={faTrash} />
          </SlimButton>
        </Box>
        <ConfirmDialog
          title="Are you sure?"
          isOpen={isConfirmDialogOpen}
          onCancel={closeConfirmDialog}
          onDelete={() => {
            removeRow(index);
            closeConfirmDialog();
          }}
        />
      </Grid>
    </>
  );
}

function BusinessPaymentPreference(props: PaymentPreferenceRowProps) {
  const { index, name, namespace, removeRow } = props;
  const ns = withNamespace(withNamespace(namespace, name), index.toString());
  const businessIdsFieldName = withNamespace(ns, "businessIds");
  const businessIds: string[] = useFieldValue(businessIdsFieldName) || [];

  const [isConfirmDialogOpen, openConfirmDialog, closeConfirmDialog] =
    useBoolean();
  return (
    <>
      <Grid item xs={5} md={3}>
        <BankingDetailField label="Bank Account" namespace={ns} required />
      </Grid>

      <FieldArray name={businessIdsFieldName}>
        {businessArrayHelpers => (
          <>
            {businessIds.map((_businessId: string, bIdx: number) => (
              <React.Fragment key={_businessId || bIdx}>
                <Grid item xs={11}>
                  <BusinessField
                    label={bIdx === 0 ? "Business" : ""}
                    name={withNamespace(businessIdsFieldName, bIdx.toString())}
                    allowAddNew={false}
                    required
                  />
                </Grid>
                <Grid item xs={1} alignContent={"flex-end"}>
                  <SlimButton
                    onClick={() => businessArrayHelpers.remove(bIdx)}
                    backgroundColor="deleteRed"
                    type="button"
                  >
                    <ButtonIcon icon={faTrash} />
                  </SlimButton>
                </Grid>
              </React.Fragment>
            ))}
            <Grid item xs={12}>
              <Box gridGap={4} display={"flex"}>
                <SlimButton
                  onClick={() => businessArrayHelpers.push(null)}
                  type="button"
                >
                  <ButtonIcon icon={faPlus} />
                  &nbsp;Add Business
                </SlimButton>

                <SlimButton
                  onClick={openConfirmDialog}
                  backgroundColor="deleteRed"
                  type="button"
                >
                  <ButtonIcon icon={faTrash} />
                  &nbsp;Delete Preference
                </SlimButton>
              </Box>
              <ConfirmDialog
                title="Are you sure?"
                isOpen={isConfirmDialogOpen}
                onCancel={closeConfirmDialog}
                onDelete={() => {
                  removeRow(index);
                  closeConfirmDialog();
                }}
              />
            </Grid>
          </>
        )}
      </FieldArray>

      <Grid xs={12} item>
        <Divider />
        <SpaceBetweenRow />
      </Grid>
    </>
  );
}

const generateDefaultPaymentPreference = () => ({
  id: uuidv4(),
  saleType: null,
  speciesId: null,
  saleSubTypeId: null,
  bankingDetailId: null,
  businessIds: [],
});

const generateDefaultBusinessPaymentPreference = () => ({
  id: uuidv4(),
  saleType: null,
  speciesId: null,
  saleSubTypeId: null,
  bankingDetailId: null,
  businessIds: [null],
});

export const PaymentPreferencesSection = ({
  sectionHelper,
  namespace: ns,
}: SectionProps) => {
  const { isOpen, onToggle } = useSectionHelper(
    sectionHelper,
    FinanceSubModalSection.PAYMENT_PREFERENCES,
  );

  const hasBusinessPermission = useHasPaymentPreferenceForBusinessPermission();

  const formikProps = useFormikContext();
  const { submitCount } = formikProps;

  const name = "paymentPreferences";

  const header = (
    <Row>
      <SubtleBadge>Payment Preferences </SubtleBadge>
    </Row>
  );

  const prefsFieldName = withNamespace(ns, name);
  const hasErrors =
    submitCount > 0 && sectionHasErrors(formikProps, [prefsFieldName]);

  const paymentPreferences: PaymentPreference[] =
    useFieldValue(prefsFieldName) || [];
  const billingSettingsId = useSelector(getDefaultBillingSettings)?.id;

  if (!billingSettingsId) {
    return null;
  }

  // group the preferences by their type and maintain the index.

  const { salePreferences, businessPreferences } = paymentPreferences.reduce(
    (acc, cur, index) => {
      if (cur.businessIds?.length > 0) {
        acc.businessPreferences.push({ id: cur.id, index });
      } else {
        acc.salePreferences.push({ id: cur.id, index });
      }
      return acc;
    },
    { salePreferences: [], businessPreferences: [] },
  );

  return (
    <FormCollapse
      isOpen={isOpen}
      onToggle={onToggle}
      hasErrorBadge={hasErrors}
      header={header}
    >
      <FieldArray name={withNamespace(ns, "paymentPreferences")}>
        {arrayHelpers => {
          const addNewSalePreference = () => {
            arrayHelpers.push(generateDefaultPaymentPreference());
          };

          const addNewBusiness = () => {
            arrayHelpers.push(generateDefaultBusinessPaymentPreference());
          };

          return (
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Paper>
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <Box display="flex" justifyContent="space-between">
                        <h3>Sale Payment Preferences</h3>
                        <Tooltip title="Add preferences for when a Bank Account will be displayed on an Invoice/used for receiving payments." />
                      </Box>
                    </Grid>

                    {salePreferences.length > 0 && (
                      <>
                        <Grid item xs={6} md={3}>
                          <Label>Sale Type</Label>
                        </Grid>
                        <Grid item xs={6} md={2}>
                          <Label>Species</Label>
                        </Grid>
                        <Grid item xs={6} md={3}>
                          <Label>Sale Sub Type</Label>
                        </Grid>
                        <Grid item xs={5} md={3}>
                          <Label required>Bank Account</Label>
                        </Grid>
                        <Grid item xs={1} md={1}>
                          &nbsp;
                        </Grid>

                        {salePreferences.map(({ id, index }) => (
                          <SaleTypePaymentPreference
                            key={id || index}
                            index={index}
                            removeRow={arrayHelpers.remove}
                            name={name}
                            namespace={ns}
                          />
                        ))}
                      </>
                    )}
                    <Grid item xs={12}>
                      <SlimButton onClick={addNewSalePreference} type="button">
                        <ButtonIcon icon={faPlus} />
                        &nbsp;New Sale Payment Preference
                      </SlimButton>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {hasBusinessPermission && (
                <Grid item xs={12}>
                  <Paper>
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <Box display="flex" justifyContent="space-between">
                          <h3>Business Payment Preferences</h3>
                          <Tooltip title="Add preferences for when a Bank Account will be displayed on an Invoice/used for receiving payments for a particular business.  This will override any sale preferences." />
                        </Box>
                      </Grid>

                      {businessPreferences.map(({ id, index }) => (
                        <BusinessPaymentPreference
                          key={id || index}
                          index={index}
                          removeRow={arrayHelpers.remove}
                          name={name}
                          namespace={ns}
                        />
                      ))}

                      <Grid item xs={12}>
                        <SlimButton onClick={addNewBusiness} type="button">
                          <ButtonIcon icon={faPlus} />
                          &nbsp;New Business Payment Preference
                        </SlimButton>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              )}
            </Grid>
          );
        }}
      </FieldArray>
    </FormCollapse>
  );
};
